/**
 * HistoryStackManager - простий менеджер історії для карточок ресурсів
 *
 * Логіка:
 * 1. При загрузці сторінки запам'ятовує поточний URL як базовий
 * 2. Drawer/Modal карточки додаються в стек без зміни URL браузера
 * 3. Page карточки обнуляють стек і стають новим базовим URL
 * 4. Закриття карточок відкочує стек у зворотному порядку
 */

import { resourceManager } from '../Managers/Resource/ResourceManager'

export class HistoryStackManager {
    private static instance: HistoryStackManager
    private historyStack: string[] = [] // Стек URL'ів у порядку відкриття
    private listeners: Set<(stack: string[]) => void> = new Set()

    private constructor() {
        // Ініціалізація тільки на клієнті
        if (typeof window !== 'undefined') {
            this.initializeBaseUrl()
        }
    }

    public static getInstance(): HistoryStackManager {
        if (!HistoryStackManager.instance) {
            HistoryStackManager.instance = new HistoryStackManager()
        }
        return HistoryStackManager.instance
    }

    /**
     * Ініціалізація базового URL при завантаженні сторінки
     */
    private initializeBaseUrl(): void {
        if (typeof window === 'undefined') return

        const currentUrl = window.location.pathname
        if (this.historyStack.length === 0) {
            this.historyStack.push(currentUrl)
        }
    }

    /**
     * Додає URL карточки в стек (для Drawer/Modal режимів)
     */
    public pushDrawerCard(url: string): void {
        if (typeof window === 'undefined') {
            console.warn('HistoryStackManager: window недоступний (SSR режим)')
            return
        }

        this.historyStack.push(url)
        this.notifyListeners()
    }

    /**
     * Встановлює новий базовий URL (для Page режиму)
     * Обнуляє весь стек і встановлює новий URL як перший
     */
    public setPageCard(url: string): void {
        if (typeof window === 'undefined') {
            console.warn('HistoryStackManager: window недоступний (SSR режим)')
            return
        }

        // Обнуляємо стек
        this.historyStack = []
        // Встановлюємо новий базовый URL
        this.historyStack.push(url)

        this.notifyListeners()
    }

    /**
     * Закриває останню карточку зі стеку
     * Повертає URL попередньої карточки або null якщо стек порожній
     */
    public popCard(): string | null {
        if (typeof window === 'undefined') {
            console.warn('HistoryStackManager: window недоступний (SSR режим)')
            return null
        }

        if (this.historyStack.length <= 1) {
            // Не можемо закрити базовий URL
            return null
        }

        // Видаляємо останню карточку
        const closedUrl = this.historyStack.pop()
        const previousUrl = this.historyStack[this.historyStack.length - 1]

        this.notifyListeners()

        return previousUrl
    }

    /**
     * Отримує поточний стек URL'ів
     */
    public getHistoryStack(): string[] {
        return [...this.historyStack]
    }

    /**
     * Отримує поточний рівень в стеку (кількість відкритих карточок)
     */
    public getCurrentLevel(): number {
        return Math.max(0, this.historyStack.length - 1)
    }

    /**
     * Перевіряє чи є карточки у стеку (окрім базової сторінки)
     */
    public hasCards(): boolean {
        return this.historyStack.length > 1
    }

    /**
     * Отримує базовий URL (перший в стеку)
     */
    public getBaseUrl(): string | null {
        return this.historyStack.length > 0 ? this.historyStack[0] : null
    }

    /**
     * Очищає весь стек до базової сторінки
     */
    public clearToBase(): void {
        if (typeof window === 'undefined') {
            console.warn('HistoryStackManager: window недоступний (SSR режим)')
            return
        }

        if (this.historyStack.length > 0) {
            const baseUrl = this.historyStack[0]
            this.historyStack = [baseUrl]
            this.notifyListeners()
        }
    }

    /**
     * Підписка на зміни стека
     */
    public subscribe(listener: (stack: string[]) => void): () => void {
        this.listeners.add(listener)
        return () => {
            this.listeners.delete(listener)
        }
    }

    /**
     * Отримує URL для картки ресурсу
     * 
     * ВАЖЛИВО: Create та edit НЕ мають URL - відкриваються через drawer/modal
     * Тільки list та view мають реальні URL
     * 
     * @param resource - Назва ресурсу (string) або об'єкт конфігурації ресурсу (IResourceConfig)
     * @param mode - Режим відкриття карточки
     * @param primaryFieldValue - Значення первинного ключа (для view режиму)
     * @returns URL карточки або null якщо pageUrl не вказаний, або для create/edit режимів
     */
    public buildResourceUrl(
        resource: string | { key?: string; pageUrl?: string },
        mode: 'list' | 'view' | 'edit' | 'create',
        primaryFieldValue?: string | number
    ): string | null {
        const baseUrl = this.getResourceBaseUrl(resource)

        // Якщо базовий URL null (pageUrl не вказаний) - повертаємо null
        if (baseUrl === null) {
            return null
        }

        switch (mode) {
            case 'list':
                return baseUrl
            case 'view':
                return `${baseUrl}/${primaryFieldValue}`
            case 'create':
            case 'edit':
                // Create та edit НЕ мають URL - відкриваються через drawer/modal
                return null
            default:
                return baseUrl
        }
    }

    /**
     * Безпечне повернення назад з урахуванням режиму відображення
     * Якщо немає попереднього стану - переходить до списку ресурсу
     */
    public safeGoBack(currentResourceName: string, displayMode: 'page' | 'drawer' | 'modal' = 'page'): void {
        if (typeof window === 'undefined') {
            console.warn('HistoryStackManager: window недоступний (SSR режим)')
            return
        }

        // Якщо це overlay (drawer/modal) - закриваємо через ResourceCardManager
        if (displayMode === 'drawer' || displayMode === 'modal') {
            // Для drawer/modal використовуємо кастомну подію закриття
            window.dispatchEvent(new CustomEvent('closeOverlay', { detail: { resourceName: currentResourceName } }))
            return
        }

        // Для page режиму перевіряємо чи є куди повернутися в стеку
        if (this.historyStack.length > 1) {
            const previousUrl = this.popCard()
            if (previousUrl) {
                window.location.href = previousUrl
            } else {
                // Fallback до списку ресурсу (якщо є pageUrl)
                const listUrl = this.buildResourceUrl(currentResourceName, 'list')
                if (listUrl) {
                    window.location.href = listUrl
                } else {
                    console.warn(`[HistoryStackManager] Неможливо повернутися назад: ресурс "${currentResourceName}" не має pageUrl`)
                }
            }
        } else {
            // Якщо немає історії - переходимо до списку ресурсу (якщо є pageUrl)
            const listUrl = this.buildResourceUrl(currentResourceName, 'list')
            if (listUrl) {
                window.location.href = listUrl
            } else {
                console.warn(`[HistoryStackManager] Неможливо повернутися назад: ресурс "${currentResourceName}" не має pageUrl`)
            }
        }
    }

    /**
     * Розбирає URL і визначає контекст ресурсу
     * Тільки list та view мають URL
     */
    public parseResourceUrl(url: string): Partial<{ resourceName: string; resourceId?: string; mode: string }> | null {
        // Парсимо URL виду: /products (list), /products/123 (view)
        // Create та edit НЕ мають URL
        const match = url.match(/^\/([^\/]+)(?:\/(\d+))?$/)

        if (!match) return null

        const [, resourceName, resourceId] = match

        if (resourceId) {
            return {
                resourceName,
                resourceId,
                mode: 'view',
            }
        }

        return {
            resourceName,
            mode: 'list',
        }
    }

    /**
     * Отримує базовий URL для ресурсу
     * 
     * @param resource - Назва ресурсу (string) або об'єкт конфігурації ресурсу
     * @returns Базовий URL ресурсу або null якщо pageUrl не вказаний
     */
    private getResourceBaseUrl(resource: string | { key?: string; pageUrl?: string }): string | null {
        // Якщо передано об'єкт ресурсу з pageUrl - використовуємо його напряму
        if (typeof resource === 'object' && resource.pageUrl) {
            return resource.pageUrl
        }
        
        // Якщо передано строку або об'єкт без pageUrl - шукаємо в ResourceManager
        const resourceName = typeof resource === 'string' ? resource : resource.key || 'unknown'
        
        const resourceConfig = resourceManager.get(resourceName)
        
        if (resourceConfig && resourceConfig.pageUrl) {
            return resourceConfig.pageUrl
        }
        
        // Якщо pageUrl не вказаний - повертаємо null
        return null
    }

    /**
     * Сповіщає слухачів про зміни
     */
    private notifyListeners(): void {
        this.listeners.forEach((listener) => {
            listener(this.getHistoryStack())
        })
    }
}

// Ледаче створення інстансу для SSR-сумісності
let _instance: HistoryStackManager | null = null

export const historyStackManager = {
    getInstance(): HistoryStackManager {
        if (!_instance) {
            _instance = HistoryStackManager.getInstance()
        }
        return _instance
    },

    // Проксі методи для зручності
    pushDrawerCard: (url: string) => {
        return historyStackManager.getInstance().pushDrawerCard(url)
    },

    setPageCard: (url: string) => {
        return historyStackManager.getInstance().setPageCard(url)
    },

    popCard: () => {
        return historyStackManager.getInstance().popCard()
    },

    getHistoryStack: () => {
        return historyStackManager.getInstance().getHistoryStack()
    },

    getCurrentLevel: () => {
        return historyStackManager.getInstance().getCurrentLevel()
    },

    hasCards: () => {
        return historyStackManager.getInstance().hasCards()
    },

    getBaseUrl: () => {
        return historyStackManager.getInstance().getBaseUrl()
    },

    clearToBase: () => {
        return historyStackManager.getInstance().clearToBase()
    },

    subscribe: (listener: (stack: string[]) => void) => {
        return historyStackManager.getInstance().subscribe(listener)
    },

    buildResourceUrl: (
        resource: string | { key?: string; pageUrl?: string },
        mode: 'list' | 'view' | 'edit' | 'create',
        primaryFieldValue?: string | number
    ): string | null => {
        return historyStackManager.getInstance().buildResourceUrl(resource, mode, primaryFieldValue)
    },

    safeGoBack: (currentResourceName: string, displayMode: 'page' | 'drawer' | 'modal' = 'page') => {
        return historyStackManager.getInstance().safeGoBack(currentResourceName, displayMode)
    },

    parseResourceUrl: (url: string) => {
        return historyStackManager.getInstance().parseResourceUrl(url)
    },
}
